/**
 * Work templates — the bundles a shop sells over and over (PRD v2 §2, "unlimited
 * templates" against Jobber's per-tier caps).
 *
 * A leaf module like schemas/request.ts and schemas/costing.ts: it takes the idempotency
 * envelope from the spine and the line shape from money, and nothing imports it back, so
 * it cannot close a cycle.
 */
import { z } from 'zod';
import { idempotency } from './spine';
import { lineItemInput } from './money';

/** What a template can start. `both` is the default — most work is either. */
export const TEMPLATE_TARGETS = ['quote', 'job', 'both'] as const;
export type TemplateTarget = (typeof TEMPLATE_TARGETS)[number];

const optionalText = (max: number) =>
  z.string().trim().max(max).optional().or(z.literal(''));

export const templateCreateInput = idempotency.extend({
  name: z.string().trim().min(1).max(120),
  appliesTo: z.enum(TEMPLATE_TARGETS).optional(),
  title: z.string().trim().min(1).max(200),
  description: optionalText(4000),
  taxRateBps: z.coerce.number().int().min(0).max(20_000).optional(),
  lines: z.array(lineItemInput).max(200).optional(),
});
export type TemplateCreateInput = z.infer<typeof templateCreateInput>;

/** Capture a quote you already wrote — the moment a template is worth saving. */
export const templateFromQuoteInput = idempotency.extend({
  quoteId: z.string().uuid(),
  name: z.string().trim().min(1).max(120),
  appliesTo: z.enum(TEMPLATE_TARGETS).optional(),
});
export type TemplateFromQuoteInput = z.infer<typeof templateFromQuoteInput>;

export const templateIdInput = idempotency.extend({
  templateId: z.string().uuid(),
});
export type TemplateIdInput = z.infer<typeof templateIdInput>;
